import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  loadUserPreferences,
  resolveColorModeFromThemePreference,
} from '@/hooks/useUserPreferences';

type Theme = 'standard' | 'bauhaus' | 'severance';
type ColorMode = 'light' | 'dark';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultColorMode?: ColorMode;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  colorMode: ColorMode;
  setTheme: (theme: Theme) => void;
  setColorMode: (colorMode: ColorMode) => void;
}

const ThemeProviderContext = createContext<ThemeProviderState | null>(null);

function applyThemeVariantToDocument(theme: Theme): void {
  const root = window.document.documentElement;

  if (theme === 'bauhaus') {
    root.setAttribute('data-theme', 'bauhaus');
  } else if (theme === 'severance') {
    root.setAttribute('data-theme', 'severance');
  } else {
    root.removeAttribute('data-theme');
  }
}

function applyColorModeToDocument(colorMode: ColorMode): void {
  const root = window.document.documentElement;

  if (colorMode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// Helper function to safely parse stored theme data
const parseStoredThemeData = (stored: string | null) => {
  if (!stored) return { theme: 'standard' as Theme, colorMode: 'light' as ColorMode };

  try {
    const parsed = JSON.parse(stored);
    // Handle new format
    if (typeof parsed === 'object' && parsed !== null) {
      return {
        theme: parsed.theme || 'standard',
        colorMode: parsed.colorMode || 'light',
      };
    }
  } catch {
    // Handle old format (just a theme string)
    return {
      theme: stored as Theme,
      colorMode: 'light' as ColorMode,
    };
  }

  return { theme: 'standard' as Theme, colorMode: 'light' as ColorMode };
};

function getInitialColorMode(defaultColorMode: ColorMode): ColorMode {
  const userPrefs = loadUserPreferences();
  return resolveColorModeFromThemePreference(userPrefs.theme) ?? defaultColorMode;
}

export function ThemeProvider({
  children,
  defaultTheme = 'standard',
  defaultColorMode = 'light',
  storageKey = 'alinea-ui-theme',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const { theme: storedTheme } = parseStoredThemeData(localStorage.getItem(storageKey));
    return storedTheme || defaultTheme;
  });

  const [colorMode, setColorModeState] = useState<ColorMode>(() => {
    const initial = getInitialColorMode(defaultColorMode);
    if (typeof document !== 'undefined') {
      applyColorModeToDocument(initial);
    }
    return initial;
  });

  useEffect(() => {
    applyThemeVariantToDocument(theme);
  }, [theme]);

  useEffect(() => {
    applyColorModeToDocument(colorMode);
  }, [colorMode]);

  const persistThemeState = useCallback(
    (nextTheme: Theme, nextColorMode: ColorMode) => {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          theme: nextTheme,
          colorMode: nextColorMode,
        }),
      );
    },
    [storageKey],
  );

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      applyThemeVariantToDocument(newTheme);
      persistThemeState(newTheme, colorMode);
    },
    [colorMode, persistThemeState],
  );

  const setColorMode = useCallback(
    (newColorMode: ColorMode) => {
      setColorModeState(newColorMode);
      applyColorModeToDocument(newColorMode);
      persistThemeState(theme, newColorMode);
    },
    [persistThemeState, theme],
  );

  const value = useMemo(
    () => ({
      theme,
      colorMode,
      setTheme,
      setColorMode,
    }),
    [theme, colorMode, setTheme, setColorMode],
  );

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (!context) throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
